import type { EventType, RealtimeMessage } from '@atlas/types'
import { useSyncExternalStore } from 'react'
import { logger } from '@/lib/logger'

// Fan-out point for realtime messages (ADR-0020), and the only thing consumers
// touch: nothing here imports MQTT or a WebSocket. The transport calls INTO
// `dispatchRealtimeMessage` / `setRealtimeConnectionState`, so a feature
// subscribes without knowing a transport exists and the transport can be
// swapped or absent entirely.
//
// A module store rather than context, following `auth/auth-error-store.ts`:
// `subscribeRealtime` must be callable from a plain module or a mutation
// callback, not only from a component.

const log = logger.child('realtime')

/** Health of the underlying connection, as reported by the transport. */
export enum RealtimeConnectionState {
	CONNECTED = 'connected',
	RECONNECTING = 'reconnecting',
	OFFLINE = 'offline',
}

/**
 * Narrows which messages reach a handler. Every field is optional and every
 * present field must match (AND); an array matches if any of its members does.
 * An empty or omitted filter therefore matches every message — the widest
 * subscription, not the narrowest.
 */
export interface RealtimeFilter {
	type?: EventType | EventType[]
	entityRef?: {
		type?: string
		id?: string | string[]
	}
}

export type RealtimeHandler = (message: RealtimeMessage) => void

interface SubscriberEntry {
	filter: RealtimeFilter
	handler: RealtimeHandler
}

// A Set of freshly allocated entry objects, so two identical (filter, handler)
// pairs are two subscriptions and each unsubscribe removes exactly its own.
// That is what makes StrictMode's subscribe → unsubscribe → subscribe leave one
// live subscription rather than zero or two.
const subscribers = new Set<SubscriberEntry>()

let connectionState: RealtimeConnectionState = RealtimeConnectionState.OFFLINE
const connectionListeners = new Set<() => void>()

/**
 * Registers a handler for messages matching `filter`. Callable from anywhere —
 * a hook, a mutation callback, or a plain module.
 *
 * @param {RealtimeFilter} filter - Criteria a message must satisfy; empty matches everything.
 * @param {RealtimeHandler} handler - Invoked with each matching message.
 * @returns {() => void} Unsubscribe function. Idempotent — calling it more than once is safe.
 */
export function subscribeRealtime(filter: RealtimeFilter, handler: RealtimeHandler): () => void {
	const entry: SubscriberEntry = { filter, handler }
	subscribers.add(entry)
	return () => {
		subscribers.delete(entry)
	}
}

/**
 * Fans a message out to every subscriber whose filter matches.
 *
 * Iterates a snapshot so a handler that subscribes or unsubscribes during
 * dispatch cannot alter the current delivery, and isolates each handler so one
 * throwing does not rob the others of the message.
 *
 * @param {RealtimeMessage} message - Message received from the transport.
 */
export function dispatchRealtimeMessage(message: RealtimeMessage): void {
	for (const entry of [...subscribers]) {
		if (!matchesFilter(entry.filter, message)) continue
		try {
			entry.handler(message)
		} catch (error) {
			log.error('Realtime handler threw; continuing with remaining handlers', error)
		}
	}
}

/**
 * Reports the transport's connection health. Called by the provider, not by
 * consumers.
 *
 * @param {RealtimeConnectionState} next - The new connection state.
 */
export function setRealtimeConnectionState(next: RealtimeConnectionState): void {
	if (next === connectionState) return
	connectionState = next
	for (const listener of connectionListeners) listener()
}

/**
 * Reads the current connection state outside React.
 *
 * @returns {RealtimeConnectionState} The current connection state.
 */
export function getRealtimeConnectionState(): RealtimeConnectionState {
	return connectionState
}

/**
 * Subscribes a component to the connection state — the signal a consumer uses
 * to choose between push and its polling fallback.
 *
 * @returns {RealtimeConnectionState} The current connection state, re-rendering on change.
 */
export function useRealtimeConnectionState(): RealtimeConnectionState {
	return useSyncExternalStore(
		(cb) => {
			connectionListeners.add(cb)
			return () => {
				connectionListeners.delete(cb)
			}
		},
		getRealtimeConnectionState,
		getRealtimeConnectionState,
	)
}

/**
 * Tests a message against one filter. Absent criteria are not constraints, so
 * an empty filter matches everything.
 *
 * @param {RealtimeFilter} filter - The subscriber's criteria.
 * @param {RealtimeMessage} message - The message being dispatched.
 * @returns {boolean} Whether every present criterion matches.
 */
function matchesFilter(filter: RealtimeFilter, message: RealtimeMessage): boolean {
	if (filter.type !== undefined && !matchesValue(filter.type, message.type)) return false

	const entityRef = filter.entityRef
	if (entityRef === undefined) return true
	if (entityRef.type !== undefined && entityRef.type !== message.entityRef.type) return false
	if (entityRef.id !== undefined && !matchesValue(entityRef.id, message.entityRef.id)) return false

	return true
}

/**
 * Compares a criterion that may be a single value or a set of accepted values.
 *
 * @param {T | T[]} criterion - One accepted value, or several.
 * @param {T} actual - The message's value.
 * @returns {boolean} Whether `actual` is (or is among) the accepted value(s).
 */
function matchesValue<T>(criterion: T | T[], actual: T): boolean {
	return Array.isArray(criterion) ? criterion.includes(actual) : criterion === actual
}
